const MOBILE_BREAKPOINT = 768;
const DRAG_THRESHOLD_PX = 6;
const SNAP_THRESHOLD = 0.15;
const RESIZE_DEBOUNCE_MS = 150;

const instances = new Map();

class ArduinoCarousel {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('.ino-cr__viewport');
    this.track = root.querySelector('.ino-cr__track');
    this.prevBtn = root.querySelector('.ino-cr__arrow--prev');
    this.nextBtn = root.querySelector('.ino-cr__arrow--next');
    this.dotsEl = root.querySelector('.ino-cr__dots');

    this.desktopPerPage = parseInt(root.dataset.slidesPerPage, 10) || 1;
    this.mobilePerPage = parseInt(root.dataset.slidesPerPageMobile, 10) || 1;
    this.currentPage = 0;

    this.dragState = null;
    this.didDrag = false;

    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onResize = this.debounce(this.onResize.bind(this), RESIZE_DEBOUNCE_MS);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);

    this.bind();
    this.layout();
  }

  get slides() {
    return Array.from(this.track.children);
  }

  perPage() {
    return window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT + 'px)').matches
      ? this.mobilePerPage
      : this.desktopPerPage;
  }

  pageCount() {
    return Math.max(1, Math.ceil(this.slides.length / this.perPage()));
  }

  bind() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onResize);

    if (this.viewport) {
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('click', this.onClickCapture, true);
    }
  }

  destroy() {
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
    window.removeEventListener('resize', this.onResize);

    if (this.viewport) {
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport.removeEventListener('click', this.onClickCapture, true);
    }
  }

  layout() {
    this.renderDots();
    this.goTo(Math.min(this.currentPage, this.pageCount() - 1));
  }

  renderDots() {
    if (!this.dotsEl) return;
    const count = this.pageCount();
    this.dotsEl.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const b = document.createElement('button');
      b.className = 'ino-cr__dot';
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', 'Go to page ' + (i + 1));
      b.addEventListener('click', () => this.goTo(i));
      this.dotsEl.appendChild(b);
    }
  }

  goTo(page) {
    const max = this.pageCount() - 1;
    const clamped = Math.max(0, Math.min(page, max));
    this.currentPage = clamped;
    this.track.style.transform = 'translateX(' + (-100 * clamped) + '%)';
    this.updateDots();
    this.updateArrows();
  }

  updateDots() {
    if (!this.dotsEl) return;
    Array.from(this.dotsEl.children).forEach((dot, i) => {
      dot.classList.toggle('ino-cr__dot--active', i === this.currentPage);
      dot.setAttribute('aria-selected', i === this.currentPage ? 'true' : 'false');
    });
  }

  updateArrows() {
    const max = this.pageCount() - 1;
    if (this.prevBtn) this.prevBtn.disabled = this.currentPage <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentPage >= max;
  }

  onPrev() { this.goTo(this.currentPage - 1); }
  onNext() { this.goTo(this.currentPage + 1); }
  onResize() { this.layout(); }

  onPointerDown(e) {
    if (e.button !== undefined && e.button !== 0) return;
    this.dragState = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startPage: this.currentPage,
      viewportWidth: this.viewport.offsetWidth || 1,
    };
    this.didDrag = false;
    try { this.viewport.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  }

  onPointerMove(e) {
    if (!this.dragState || e.pointerId !== this.dragState.pointerId) return;
    const dx = e.clientX - this.dragState.startX;
    if (!this.didDrag && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
    this.didDrag = true;
    this.track.classList.add('ino-cr__track--dragging');
    this.track.style.transform =
      'translateX(calc(' + (-100 * this.dragState.startPage) + '% + ' + dx + 'px))';
  }

  onPointerUp(e) {
    if (!this.dragState || e.pointerId !== this.dragState.pointerId) return;
    const { startX, startPage, viewportWidth } = this.dragState;
    const dx = e.clientX - startX;
    const delta = dx / viewportWidth;

    this.track.classList.remove('ino-cr__track--dragging');
    try { this.viewport.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    this.dragState = null;

    if (!this.didDrag) {
      this.goTo(startPage);
      return;
    }

    if (delta > SNAP_THRESHOLD) {
      this.goTo(startPage - 1);
    } else if (delta < -SNAP_THRESHOLD) {
      this.goTo(startPage + 1);
    } else {
      this.goTo(startPage);
    }
  }

  onClickCapture(e) {
    if (this.didDrag) {
      e.preventDefault();
      e.stopPropagation();
      this.didDrag = false;
    }
  }

  goToBlock(blockEl) {
    const slideEl = blockEl.closest('.ino-slide');
    if (!slideEl) return;
    const idx = this.slides.indexOf(slideEl);
    if (idx < 0) return;
    this.goTo(Math.floor(idx / this.perPage()));
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

function initRoot(root) {
  if (!root || instances.has(root)) return;
  instances.set(root, new ArduinoCarousel(root));
}

function destroyRoot(root) {
  const inst = instances.get(root);
  if (!inst) return;
  inst.destroy();
  instances.delete(root);
}

function initAll(scope) {
  const root = scope || document;
  if (root.matches && root.matches('.ino-cr')) initRoot(root);
  if (root.querySelectorAll) root.querySelectorAll('.ino-cr').forEach(initRoot);
}

function destroyAll(scope) {
  const root = scope;
  if (!root) return;
  if (root.matches && root.matches('.ino-cr')) destroyRoot(root);
  if (root.querySelectorAll) root.querySelectorAll('.ino-cr').forEach(destroyRoot);
}

document.addEventListener('DOMContentLoaded', () => initAll());
if (document.readyState !== 'loading') initAll();

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
document.addEventListener('shopify:block:load', (e) => initAll(e.target));
document.addEventListener('shopify:block:unload', (e) => destroyAll(e.target));

document.addEventListener('shopify:block:select', (e) => {
  const root = e.target.closest('.ino-cr');
  if (!root) return;
  const inst = instances.get(root);
  if (inst) inst.goToBlock(e.target);
});
